// src/components/ShareModal.tsx
import { useState, useEffect } from 'react';
import { IoClose, IoLogoWhatsapp, IoMail, IoLink, IoCopy, IoCheckmark } from 'react-icons/io5';
import { FaInstagram } from 'react-icons/fa';
import toast from 'react-hot-toast';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    productName: string;
    productUrl: string;
    productImage?: string;
}

const ShareModal = ({ isOpen, onClose, productName, productUrl, productImage }: ShareModalProps) => {
    const [copied, setCopied] = useState(false);
    const [animateIn, setAnimateIn] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Small delay for animation
            setTimeout(() => setAnimateIn(true), 10);
        } else {
            setAnimateIn(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const shareMessage = `Check out this amazing product from Astroji! ${productName} - Shop now: ${productUrl}`;
    const emailSubject = `Check out ${productName} on Astroji!`;
    const emailBody = `Hi!\n\nI found this amazing product on Astroji that I thought you'd love:\n\n${productName}\n\nCheck it out here: ${productUrl}\n\nHappy Shopping!`;

    const handleWhatsAppShare = () => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        onClose();
    };

    const handleInstagramShare = () => {
        // Instagram doesn't have a direct share URL, so we copy to clipboard and redirect
        navigator.clipboard.writeText(shareMessage);
        toast.success('Link copied! Opening Instagram...', { duration: 2000 });
        setTimeout(() => {
            window.open('https://instagram.com', '_blank', 'noopener,noreferrer');
        }, 500);
        onClose();
    };

    const handleEmailShare = () => {
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        window.location.href = mailtoUrl;
        onClose();
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(productUrl);
        setCopied(true);
        toast.success('Link copied to clipboard!', { duration: 2000 });
        setTimeout(() => {
            setCopied(false);
            onClose();
        }, 1000);
    };

    const shareOptions = [
        {
            name: 'WhatsApp',
            icon: <IoLogoWhatsapp className="text-2xl" />,
            color: 'bg-green-500',
            hoverColor: 'hover:bg-green-600',
            onClick: handleWhatsAppShare,
        },
        {
            name: 'Instagram',
            icon: <FaInstagram className="text-2xl" />,
            color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400',
            hoverColor: 'hover:opacity-90',
            onClick: handleInstagramShare,
        },
        {
            name: 'Email',
            icon: <IoMail className="text-2xl" />,
            color: 'bg-blue-500',
            hoverColor: 'hover:bg-blue-600',
            onClick: handleEmailShare,
        },
        {
            name: 'Copy Link',
            icon: copied ? <IoCheckmark className="text-2xl" /> : <IoLink className="text-2xl" />,
            color: copied ? 'bg-green-500' : 'bg-gray-600',
            hoverColor: 'hover:bg-gray-700',
            onClick: handleCopyLink,
        },
    ];

    return (
        <div
            className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center transition-all duration-300 ${animateIn ? 'bg-black/50' : 'bg-transparent'}`}
            onClick={onClose}
        >
            {/* Modal Content */}
            <div
                className={`bg-white w-full sm:w-auto sm:min-w-[360px] sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl transform transition-all duration-300 ease-out ${animateIn ? 'translate-y-0 opacity-100' : 'translate-y-full sm:translate-y-8 opacity-0'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-accent">Share This Product</h3>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                    >
                        <IoClose className="text-gray-500" />
                    </button>
                </div>

                {/* Product Preview */}
                <div className="p-4 bg-gray-50">
                    <div className="flex items-center gap-3">
                        {productImage && (
                            <img
                                src={productImage}
                                alt={productName}
                                className="w-14 h-14 rounded-xl object-cover border border-gray-200"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-accent truncate">{productName}</p>
                            <p className="text-xs text-gray-500 truncate">{productUrl}</p>
                        </div>
                    </div>
                </div>

                {/* Share Options */}
                <div className="p-4">
                    <p className="text-sm text-gray-500 mb-4">Share via</p>
                    <div className="grid grid-cols-4 gap-3">
                        {shareOptions.map((option, index) => (
                            <button
                                key={option.name}
                                onClick={option.onClick}
                                className="flex flex-col items-center gap-2 group"
                                style={{ animationDelay: `${index * 50}ms` }}
                            >
                                <div
                                    className={`w-14 h-14 rounded-full ${option.color} ${option.hoverColor} text-white flex items-center justify-center shadow-lg transform transition-all duration-200 group-hover:scale-110 group-active:scale-95`}
                                >
                                    {option.icon}
                                </div>
                                <span className="text-xs font-medium text-gray-600">{option.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Copy Full Message */}
                <div className="p-4 pt-0">
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(shareMessage);
                            toast.success('Message copied!', { duration: 2000 });
                        }}
                        className="w-full py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-600 flex items-center justify-center gap-2 transition-colors"
                    >
                        <IoCopy className="text-base" />
                        Copy Message with Link
                    </button>
                </div>

                {/* Safe area for mobile */}
                <div className="h-2 sm:h-0" />
            </div>
        </div>
    );
};

export default ShareModal;
